<script setup lang="ts">
import { useRouter } from 'vue-router'
import { useDrawerStore } from '@/stores/drawer'
import { useCalculationStore } from '@/stores/calculation'
import { storeToRefs } from 'pinia'
import type {
  CalculationFormTab,
  CalculationFormTabs
} from '@/components/calculation-form/type/type'
import { computed, defineEmits, defineProps, onMounted, type PropType } from 'vue'
import { STATUS_CHOICES } from '@/components/calculation-form/constants/constants'

const props = defineProps({
  tab: { type: String as PropType<CalculationFormTab>, required: true },
  tabs: { type: Array as PropType<CalculationFormTabs>, required: true }
})

const emit = defineEmits(['updateTabValue'])

const router = useRouter()
const drawerStore = useDrawerStore()
const calculationStore = useCalculationStore()
const { drawer } = storeToRefs(drawerStore)
const { currentItem: currentCalculationItem } = storeToRefs(calculationStore)

const tab = computed({
  get: () => props.tab,
  set: (value: CalculationFormTab) => emit('updateTabValue', value)
})

const previousView = computed(() =>
  currentCalculationItem.value?.status === STATUS_CHOICES.DRAFT ? 'project' : 'calculation'
)

const disableRunCalculationButton = computed(() => {
  return props.tabs.some((tab) => !tab.isCompleted) || isReadOnly.value
})

const activeTab = (value: CalculationFormTab) => {
  return tab.value === value ? 'active-tab' : ''
}

const completeTab = (value: boolean) => {
  return value || isReadOnly.value ? 'font-weight-bold' : 'font-weight-regular'
}

const navigateToPreviousView = () => {
  if (previousView.value === 'project') {
    router.push({ name: 'project-detail', params: { id: currentCalculationItem.value?.project } })
  } else {
    router.push({
      name: 'calculation-result',
      params: {
        id: currentCalculationItem.value?.project,
        calculationId: currentCalculationItem.value?.id
      }
    })
  }
}

const celeryCalculate = async () => {
  const response = await calculationStore.celeryCalculate(currentCalculationItem.value)

  if (response) {
    await router.push({
      name: 'calculation-result',
      params: {
        id: currentCalculationItem.value.project,
        calculationId: currentCalculationItem.value.id
      }
    })
  }
}

const isReadOnly = computed(() => {
  return currentCalculationItem.value?.status !== STATUS_CHOICES.DRAFT
})

onMounted(() => {
  drawer.value.shown = true
})
</script>

<template>
  <v-navigation-drawer v-model="drawer.shown">
    <v-list class="py-2">
      <v-list-item-action class="d-flex justify-center py-2">
        <v-btn
          class="text-none my-1 mx-2"
          variant="tonal"
          prepend-icon="mdi-arrow-left"
          @click.stop="navigateToPreviousView"
        >
          Back to {{ previousView }}
        </v-btn>
      </v-list-item-action>

      <v-list-item class="px-0" color="primary">
        <v-divider />
        <v-tabs
          v-model="tab"
          bg-color="transparent"
          color="primary"
          direction="vertical"
          style="z-index: 1"
        >
          <template v-for="(item, index) in tabs" :key="item.value">
            <v-tab
              v-if="item.isShown"
              :value="item.value"
              :class="['text-none', activeTab(item.value)]"
              rounded="0"
            >
              <span :class="['px-4', activeTab(item.value), completeTab(item.isCompleted)]">
                {{ item.label }}
              </span>
              <v-icon v-if="item.isCompleted || isReadOnly" color="primary">mdi-check</v-icon>
            </v-tab>
            <v-divider v-if="index < tabs.length - 1"></v-divider>
          </template>
        </v-tabs>
        <v-divider />
      </v-list-item>

      <v-list-item-action class="d-flex justify-center py-2">
        <v-btn
          class="text-none my-1 mx-2"
          :variant="disableRunCalculationButton ? 'tonal' : 'flat'"
          :color="disableRunCalculationButton ? 'revert' : 'primary'"
          :disabled="disableRunCalculationButton"
          @click.stop="celeryCalculate"
        >
          Run calculation
        </v-btn>
      </v-list-item-action>
    </v-list>
  </v-navigation-drawer>
</template>

<style scoped>
.v-tab :deep(.v-tab__slider) {
  display: none;
}

.v-tab :deep(.v-btn__overlay) {
  display: none;
}

.active-tab {
  background-color: #ccecee;
  color: #303030;
}
</style>
